import { useEffect, useState, useRef } from "react";
import { api, formatDate, daysFromNow } from "../lib/api";
import { PageHeader, StatusBadge, Empty } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("Certification");
  const [expiry, setExpiry] = useState("");
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const loadDocs = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data || []);
    } catch {
      setDocs([]);
    }
  };
  useEffect(() => { loadDocs(); }, []);

  const upload = async () => {
    if (!name) { toast.error("Name required"); return; }
    try {
      await api.post("/documents", { name, type, expiry_date: expiry ? new Date(expiry).toISOString() : null });
      toast.success("Document uploaded");
      setName(""); setType("Certification"); setExpiry(""); setOpen(false);
      loadDocs();
    } catch { toast.error("Could not upload"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Removed"); loadDocs(); }
    catch { toast.error("Could not remove"); }
  };

  const handleFile = (file) => {
    if (file) { setName(file.name); setOpen(true); }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader title="Document Vault" subtitle="Upload and manage compliance documents" actions={[
        <Button key="up" className="bg-[#6366F1] hover:bg-[#4F46E5]" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(() => setOpen(true), 0); }} data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-1.5" /> Upload</Button>
      ]} />

      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload document</DialogTitle>
              <DialogDescription>Add a certification, registration, or contract to your vault.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Document name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="doc-name" /></div>
              <div><Label>Type</Label><Input value={type} onChange={(e) => setType(e.target.value)} className="mt-1.5" /></div>
              <div><Label>Expiry date</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1.5" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={upload} className="bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="doc-upload-submit">Upload</Button>
            </DialogFooter>
          </DialogContent>
      </Dialog>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-10 mb-6 text-center cursor-pointer transition-colors ${dragOver ? "border-[#6366F1] bg-indigo-50" : "border-zinc-300 hover:border-[#6366F1]"}`}
        data-testid="drop-zone"
      >
        <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
        <div className="text-sm font-medium">Drag & drop files here</div>
        <div className="text-xs text-zinc-500 mt-1">or click to browse · PDF, PNG, JPG up to 10MB</div>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>

      {docs === null && <Skeleton className="h-48" />}
      {docs && docs.length === 0 && <Empty title="No documents yet" subtitle="Upload your certifications, registrations, and contracts" />}

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        {docs && docs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Uploaded</th>
                <th className="px-5 py-3">Expiry</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const dd = daysFromNow(d.expiry_date);
                const expiring = dd != null && dd >= 0 && dd <= 30;
                return (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-zinc-50" data-testid="doc-row">
                    <td className="px-5 py-3 font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-400" /> {d.name}</td>
                    <td className="px-5 py-3">{d.type}</td>
                    <td className="px-5 py-3">{formatDate(d.uploaded_at)}</td>
                    <td className="px-5 py-3">
                      {formatDate(d.expiry_date)}
                      {expiring && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Expires in {dd}d</span>}
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={d.verification_status} /></td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(d.id)} className="text-red-500 hover:text-red-700" data-testid="doc-delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
